export type GpxPoint = {
  lat: number;
  lon: number;
  ele?: number;
  time: string;
};

export type GpxFieldValue = {
  track: GpxPoint[];
  recordingStarted?: string;
  isRecording: boolean;
};

export function parseGpxValue(raw: unknown): GpxFieldValue | null {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return null;
  const obj = raw as Record<string, unknown>;
  if (!Array.isArray(obj.track)) return null;
  const track: GpxPoint[] = [];
  for (const pt of obj.track) {
    if (pt == null || typeof pt !== "object") continue;
    const p = pt as Record<string, unknown>;
    if (typeof p.lat !== "number" || typeof p.lon !== "number") continue;
    if (typeof p.time !== "string") continue;
    track.push({
      lat: p.lat,
      lon: p.lon,
      ele: typeof p.ele === "number" ? p.ele : undefined,
      time: p.time,
    });
  }
  return {
    track,
    recordingStarted:
      typeof obj.recordingStarted === "string" ? obj.recordingStarted : undefined,
    isRecording: obj.isRecording === true,
  };
}

export function gpxLastFix(value: GpxFieldValue): GpxPoint | null {
  if (value.track.length === 0) return null;
  return value.track[value.track.length - 1];
}

export function gpxLastFixLabel(value: GpxFieldValue): string {
  const fix = gpxLastFix(value);
  if (!fix) return "";
  return `${fix.lat.toFixed(5)}, ${fix.lon.toFixed(5)}`;
}

export function gpxPointCount(value: GpxFieldValue): number {
  return value.track.length;
}

export function gpxTableLabel(raw: unknown): string {
  const gpx = parseGpxValue(raw);
  if (!gpx || gpx.track.length === 0) return "—";
  const label = gpxLastFixLabel(gpx);
  const count = gpx.track.length;
  const recording = gpx.isRecording ? " (live)" : "";
  return label ? `${label} — ${count} pts${recording}` : `${count} pts${recording}`;
}

export function generateGpxXml(
  value: GpxFieldValue,
  trackName = "Labwick Track"
): string {
  const points = value.track
    .map((pt) => {
      const ele = pt.ele != null ? `\n        <ele>${pt.ele}</ele>` : "";
      return `      <trkpt lat="${pt.lat}" lon="${pt.lon}">${ele}\n        <time>${pt.time}</time>\n      </trkpt>`;
    })
    .join("\n");

  return `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="Labwick" xmlns="http://www.topografix.com/GPX/1/1">
  <trk>
    <name>${escapeXml(trackName)}</name>
    <trkseg>
${points}
    </trkseg>
  </trk>
</gpx>`;
}

function escapeXml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

export function emptyGpxValue(): GpxFieldValue {
  return { track: [], isRecording: false };
}
